import React, { useContext, useEffect, useState } from 'react';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { Col, Row, Tabs, Tab } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ImSpinner4 } from 'react-icons/im';
import { BsFileText } from 'react-icons/bs';
import InfoData from '../../components/info-data';
import Breadcrumbs from '../../components/layout/breadcrumbs';
import Page from '../../components/page';
import Branch from '../../components/single-branch';
import BranchSkeleton from '../../components/branch-skeleton';
import { api } from '../../providers/apiClient';
import { ContextUser } from '../../providers/ContextUser';
import { ContextSearch } from '../../providers/ContextSearch';
import Search from '../../components/search';
import NoResults from '../../components/no-result';
import { generateReportName, applyTextFilters, hasActiveFilters } from '../../utils';
import { HorizontalSplit, BranchsContainer, ExportActionButton } from './styles';

moment();

const PageOrganization = ({ isCollapsed }) => {
  const { orgId } = useParams();
  const { handleUpdateCurrentOrganization } = useContext(ContextUser);
  const { searchValues, setSearchValues } = useContext(ContextSearch);
  const [org, setOrg] = useState();
  const [isFetching, setIsFetching] = useState(true);
  const [branches, setBranches] = useState([]);
  const [searchBrancheResult, setSearchBrancheResult] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [fetchingConsolidatedReport, setFetchingConsolidatedReport] =
    useState(false);
  const [fetchingEvolutionReport, setFetchingEvolutionReport] = useState(false);
  const [tab, setTab] = useState('branchs');

  // Carrega dados apenas uma vez na montagem do componente
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsFetching(true);
        
        const response = await api.get(`/orgs/${orgId}/branchs`);
        const data = response.data;
        
        // Atualiza estados
        setBranches(data.branchs);
        setOrg(data);
        setBreadcrumbs(data.breadcrumbs);
        handleUpdateCurrentOrganization(data.organization);
        
        // Aplica filtros salvos após carregar dados
        applyFiltersLocally(data.branchs, searchValues['org'] || {});
        
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      } finally {
        setIsFetching(false);
      }
    };

    loadData();
  }, []); // Sem dependências - executa apenas uma vez

  // Aplica filtros localmente quando searchValues mudar
  useEffect(() => {
    if (branches.length > 0) {
      if (hasActiveFilters(searchValues, 'org')) {
        applyFiltersLocally(branches, searchValues['org']);
      } else {
        // Se não há filtros ativos, mostra todas as branches
        setSearchBrancheResult(branches);
      }
    }
  }, [searchValues, branches]);

  const handleExportComplianceReport = async () => {
    setFetchingConsolidatedReport(true);
    toast.success('Gerando relatório, aguarde.');

    api
      .get(`/orgs/${orgId}/report`, {
        responseType: 'blob',
      })
      .then((response) => {
        const file = new Blob([response.data], { type: 'application/pdf' });
        
        // Usar o nome do arquivo definido no backend (Content-Disposition header)
        const contentDisposition = response.headers['content-disposition'];
        const fileName = contentDisposition 
          ? contentDisposition.split('filename=')[1] 
          : generateReportName('compliance', org.name);

        saveAs(file, fileName);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => setFetchingConsolidatedReport(false));
  };

  const handleExportEvolutionReport = async () => {
    setFetchingEvolutionReport(true);
    toast.success('Gerando relatório, aguarde.');

    api
      .get(`/orgs/${orgId}/report`, {
        responseType: 'blob',
      })
      .then((response) => {
        const file = new Blob([response.data], { type: 'application/pdf' });
        
        // Usar o nome do arquivo definido no backend (Content-Disposition header)
        const contentDisposition = response.headers['content-disposition'];
        const fileName = contentDisposition 
          ? contentDisposition.split('filename=')[1] 
          : generateReportName('evolucao', org.name);

        saveAs(file, fileName);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => setFetchingEvolutionReport(false));
  };

  const ButtonExportCompliance = (
    <ExportActionButton
      key='btnExportCompliance'
      onClick={handleExportComplianceReport}
      type='button'
      disabled={fetchingConsolidatedReport || branches.length <= 0}
      id='org-export-compliance'
      title='Exportar Relatório Compliance'
      className='action-button info'
    >
      {fetchingConsolidatedReport ? (
        <>
          <ImSpinner4 style={{ animation: 'spin 2s infinite linear' }} />
          Gerando
        </>
      ) : (
        <>
          <BsFileText />
          Relatório Compliance
        </>
      )}
    </ExportActionButton>
  );

  // Função para aplicar filtros localmente sem chamar API
  const applyFiltersLocally = (allBranches, filters) => {
    const searchFields = ['name', 'responsible.name', 'responsible.email'];
    applyTextFilters(allBranches, filters, searchFields, setSearchBrancheResult);
  };

  const ButtonExportEvolution = (
    <ExportActionButton
      key='btnExportEvolution'
      onClick={handleExportEvolutionReport}
      type='button'
      disabled
      id='org-export-evolution'
      title='Exportar Relatório de Evolução'
      className='action-button secondary'
    >
      {fetchingEvolutionReport ? (
        <>
          <ImSpinner4 style={{ animation: 'spin 2s infinite linear' }} />
          Aguarde
        </>
      ) : (
        <>
          <BsFileText />
          Relatório Evolução
        </>
      )}
    </ExportActionButton>
  );

  return (
    <>
      <Breadcrumbs path={breadcrumbs} />
      <Page
        title={org && org.name}
        sideAction={[ButtonExportCompliance, ButtonExportEvolution]}
      >
        <InfoData compliance={org?.compliance} riskMatrix={org?.riskMatrix} />
        <HorizontalSplit />
        <Tabs
          id='controlled-tabs'
          activeKey={tab}
          onSelect={(currentTab) => setTab(currentTab)}
          className='mb-3'
          style={{
            margin: '0 !important',
          }}
        >
          <Tab eventKey='branchs' title='Secretarias'>
            <BranchsContainer>
              <Search
                data={branches}
                fields={['name', 'responsible.name', 'responsible.email']}
                placeholder='Busque por secretaria ou dados do responsável...'
                setSearchResult={setSearchBrancheResult}
                onSearchTextChange={setSearchValues}
                pageName='org'
              />
              <Row style={{ width: '100%' }}>
                {isFetching ? (
                  [0, 1, 2, 3].map((item) => (
                    <Col
                      style={{ marginBottom: '20px', height: 'auto' }}
                      md={isCollapsed ? 3 : 4}
                      key={`skeleton-${item}`}
                    >
                      <BranchSkeleton id={`skeleton-branch-${item}`} />
                    </Col>
                  ))
                ) : searchBrancheResult.length > 0 ? (
                  searchBrancheResult.map((item) => (
                    <Col
                      style={{ marginBottom: '20px', height: 'auto' }}
                      md={isCollapsed ? 3 : 4}
                      key={item.id}
                    >
                      <Branch item={item} />
                    </Col>
                  ))
                ) : (
                  <NoResults
                    message='Nenhuma secretaria encrontrada'
                    searchText={searchValues['org']?.text || ''}
                  />
                )}
              </Row>
            </BranchsContainer>
          </Tab>
        </Tabs>
      </Page>
    </>
  );
};

export default PageOrganization;
